"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import NumberFlow from "@number-flow/react";
import useIsMobile from "@/hooks/use-is-mobile";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { PlanIcon } from "@/components/ui/plan-icon";
import { useAuth, useLogout } from "@/features/auth";
import { useCreditBalance, useSubscription } from "@/features/billing";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetTitle,
} from "@/components/ui/sheet";
import { Icon } from "@/components/ui/icon";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";
import { cn } from "@/lib/utils";
import { ProjectSelect } from "./project-select";
import { OrganizationSelect } from "./organization-select";
import { OrganizationSubmenu } from "./organization-submenu";
import { CreateOrganization } from "./create-organization";

const SUPPORT_MAILTO = "mailto:support@arbi.ai";

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  member: "Member",
};

const CUSTOM_PLAN_COLOR = "#F17DFB";

/** One hue per plan; drives the avatar ring, the plan chip and the plan text. */
const PLAN_RING_COLORS: Record<string, string> = {
  free: "#66e278",
  solo: "#54f5ed",
  growth: "#ffaf53",
  business: "#2b5df3",
  custom: CUSTOM_PLAN_COLOR,
};

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: "Free",
  solo: "Solo",
  growth: "Growth",
  business: "Business",
};

/**
 * Account menu. `appearance="navbar"` is the avatar-only trigger (used on
 * mobile, where it opens a sheet). `appearance="sidebar"` renders a full row
 * with name + role for the desktop sidebar footer; `collapsed` shrinks it to
 * the avatar on the icon rail.
 */
export function UserMenu({
  appearance = "navbar",
  collapsed = false,
}: {
  appearance?: "navbar" | "sidebar";
  collapsed?: boolean;
} = {}) {
  const isSidebar = appearance === "sidebar";
  const { user } = useAuth();
  const logout = useLogout();
  const router = useRouter();
  const { data: credits } = useCreditBalance();
  const { data: subscription } = useSubscription();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  // Lives outside the dropdown so it survives the menu closing on select.
  const [createOrgOpen, setCreateOrgOpen] = useState(false);

  const planSlug = subscription?.plan.slug ?? "free";
  // Custom (admin-assigned) plans have arbitrary slugs and are flagged via
  // `managed_externally`/`is_custom` — mirror current-plan-row.tsx detection so
  // they don't fall through to the free-plan defaults.
  const isCustom = Boolean(
    subscription?.managed_externally || subscription?.plan.is_custom,
  );
  const iconSlug = isCustom ? "custom" : planSlug;
  const planName = isCustom
    ? (subscription?.plan.name ?? "Custom")
    : (PLAN_DISPLAY_NAMES[planSlug] ?? subscription?.plan.name ?? "Free");
  const ringColor = isCustom
    ? CUSTOM_PLAN_COLOR
    : (PLAN_RING_COLORS[planSlug] ?? PLAN_RING_COLORS.free);

  const balance = credits?.balance ?? 0;
  const total = credits?.total_allocation ?? 0;
  const remainingPercent =
    total > 0 ? Math.round((Math.min(balance, total) / total) * 100) : 0;

  const roleLabel = user?.role
    ? (ROLE_LABELS[user.role] ??
      user.role.charAt(0).toUpperCase() + user.role.slice(1))
    : null;

  /**
   * Avatar wrapped in the plan-coloured ring. Whole-pixel ring and gap so
   * the photo sits dead centre: at 1.5px the browser rounds the two sides
   * differently. `surface` is the colour behind the ring (the gap between
   * ring and photo should match it).
   */
  const ringAvatar = (
    sizeClass: string,
    surface: "frame" | "elevated" | "primary",
    iconSize: number,
  ) => {
    const surfaceClass = {
      frame: "border-surface-frame bg-surface-frame",
      elevated: "border-surface-elevated bg-surface-elevated",
      primary: "border-surface-primary bg-surface-primary",
    }[surface];
    return (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full p-[2px]",
          sizeClass,
        )}
        style={{ background: ringColor }}
      >
        <Avatar className={cn("size-full border-2", surfaceClass)}>
          <AvatarImage src={user?.avatar_url} alt="" />
          <AvatarFallback className={surfaceClass}>
            <Icon
              name="IconPeopleCircle"
              size={iconSize}
              className="text-heading opacity-30"
            />
          </AvatarFallback>
        </Avatar>
      </span>
    );
  };

  /** Sidebar footer row / rail. */
  const sidebarAvatar = ringAvatar("size-8", "frame", 16);

  const avatarTrigger = (
    <div
      className="flex size-9 cursor-pointer items-center justify-center rounded-full p-[2px] outline-none transition-[box-shadow] duration-150 focus-visible:ring-1 focus-visible:ring-contrast/20"
      style={{ background: ringColor }}
    >
      <Avatar className="size-full border-[2px] border-surface-primary">
        <AvatarImage src={user?.avatar_url} alt="" />
        <AvatarFallback className="bg-surface-primary">
          <Icon
            name="IconPeopleCircle"
            size={18}
            className="text-heading opacity-20"
          />
        </AvatarFallback>
      </Avatar>
    </div>
  );

  /**
   * Plan as a small tag beside the name (Perplexity's "Pro" chip, Cosmos'
   * "Premium") rather than a full-width pill on its own line. Tinted from
   * the plan colour so it ties to the avatar ring.
   */
  const planChip = (
    <span
      className="inline-flex h-[18px] shrink-0 items-center gap-1 rounded-[5px] px-1.5 text-[10.5px] font-semibold leading-none"
      style={{
        color: ringColor,
        background: `color-mix(in srgb, ${ringColor} 12%, transparent)`,
        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${ringColor} 22%, transparent)`,
      }}
      title={isCustom ? planName : `${planName} plan`}
    >
      <PlanIcon
        slug={iconSlug}
        width={11}
        height={11}
        className="size-[11px]"
      />
      {planName}
    </span>
  );

  const identity = (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-2">
        {user?.full_name && (
          <span className="min-w-0 truncate text-sm font-medium leading-5 text-heading">
            {user.full_name}
          </span>
        )}
        {planChip}
      </div>
      <span className="truncate text-xs leading-4 text-contrast/45">
        {user?.email}
      </span>
    </div>
  );

  /** Dropdown header: ring avatar beside the identity block. */
  const dropdownHeader = (
    <div className="flex items-center gap-3 py-1">
      {ringAvatar("size-9", "elevated", 18)}
      <div className="min-w-0 flex-1">{identity}</div>
    </div>
  );

  const creditsBar = (
    <div className="flex w-full flex-col gap-1.5 px-2.5 py-2">
      <div className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1.5">
          <Icon
            name="IconCoin1"
            size={14}
            className="text-[#c2710a] dark:text-[#FFAF53]"
          />
          <span className="text-sm text-heading">Credits</span>
        </span>
        <span className="flex items-baseline text-xs leading-normal">
          <span className="relative inline-block tabular-nums">
            <span
              className="invisible select-none pointer-events-none"
              aria-hidden="true"
            >
              {balance.toLocaleString()}
            </span>
            <NumberFlow
              value={balance}
              className="absolute inset-0 flex items-center justify-center text-heading"
            />
          </span>
          <span className="text-contrast/50">/{total.toLocaleString()}</span>
        </span>
      </div>
      <div className="flex h-1.5 w-full items-center overflow-hidden rounded-full bg-contrast/[0.12]">
        <div
          className="h-1.5 rounded-full bg-[#FFAF53] transition-all duration-500"
          style={{
            width: `${Math.min(remainingPercent, 100)}%`,
            minWidth: remainingPercent > 0 ? "4px" : undefined,
          }}
        />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label="Open account menu"
          className="rounded-full outline-none focus-visible:ring-1 focus-visible:ring-contrast/20"
        >
          {avatarTrigger}
        </button>

        <Sheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          disableFocusTrap
          className="h-auto max-h-[85dvh]"
        >
          <SheetTitle className="sr-only">Account</SheetTitle>
          <SheetBody className="gap-5">
            <div className="flex items-start gap-3">
              <div
                className="flex size-12 shrink-0 items-center justify-center rounded-full p-[2px]"
                style={{ background: ringColor }}
              >
                <Avatar className="size-full border-[2px] border-surface-primary">
                  <AvatarImage src={user?.avatar_url} alt="" />
                  <AvatarFallback className="bg-surface-primary">
                    <Icon
                      name="IconPeopleCircle"
                      size={24}
                      className="text-heading opacity-20"
                    />
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="flex-1 min-w-0">{identity}</div>
              <SheetClose className="-mt-0.5 shrink-0" />
            </div>

            {credits && creditsBar}

            <div className="flex flex-col gap-2">
              <OrganizationSelect showLabel className="w-full max-w-none" />
              <ProjectSelect className="w-full max-w-none flex-none" />
            </div>

            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => {
                  setSheetOpen(false);
                  router.push("/settings");
                }}
                className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-heading transition-colors hover:bg-contrast/6"
              >
                <Icon
                  name="IconSettingsGear2"
                  size={16}
                  className="text-heading opacity-40"
                />
                Settings
              </button>
              <button
                type="button"
                onClick={() => {
                  setSheetOpen(false);
                  logout.mutate(undefined);
                }}
                className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-heading transition-colors hover:bg-contrast/6"
              >
                <Icon
                  name="IconArrowBoxLeft"
                  size={16}
                  className="text-heading opacity-40"
                />
                Log out
              </button>
            </div>
          </SheetBody>
        </Sheet>
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        {isSidebar ? (
          <DropdownMenuTrigger
            aria-label={
              collapsed
                ? `Account menu${user?.full_name ? ` — ${user.full_name}` : ""}`
                : undefined
            }
            className={cn(
              "group/account flex h-10 cursor-pointer items-center rounded-md outline-none",
              "transition-colors duration-150 hover:bg-contrast/4 data-[popup-open]:bg-contrast/6",
              "focus-visible:ring-2 focus-visible:ring-ai/40",
              collapsed ? "w-9 justify-center px-0" : "w-full gap-2.5 px-1.5",
            )}
          >
            {sidebarAvatar}
            {!collapsed && (
              <>
                <span className="flex min-w-0 flex-1 flex-col gap-px text-left">
                  <span className="truncate text-[13px] font-medium leading-4 text-heading">
                    {user?.full_name ?? user?.email ?? "Account"}
                  </span>
                  {roleLabel && (
                    <span className="truncate text-[11px] leading-4 text-contrast/45">
                      {roleLabel}
                    </span>
                  )}
                </span>
                <Icon
                  name="IconChevronGrabberVertical"
                  size={14}
                  className="shrink-0 text-contrast/35 transition-colors group-hover/account:text-contrast/60"
                />
              </>
            )}
          </DropdownMenuTrigger>
        ) : (
          <DropdownMenuTrigger>{avatarTrigger}</DropdownMenuTrigger>
        )}

        <DropdownMenuContent
          align={isSidebar ? "start" : "end"}
          side={isSidebar ? "top" : undefined}
          sideOffset={isSidebar ? 8 : undefined}
          className={isSidebar ? "min-w-[240px]" : "min-w-[200px]"}
        >
          <DropdownMenuLabel className="px-2 py-1.5">
            {dropdownHeader}
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          {/* Credits live in the sidebar footer on desktop; the navbar variant
            (mobile sheet aside) still surfaces them here. */}
          {credits && !isSidebar && (
            <>
              <DropdownMenuGroup className="2xl:hidden">
                {creditsBar}
              </DropdownMenuGroup>

              <DropdownMenuSeparator className="2xl:hidden" />
            </>
          )}

          <DropdownMenuGroup>
            <OrganizationSubmenu
              onCreateOrganization={() => setCreateOrgOpen(true)}
            />
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => router.push("/settings")}>
              <Icon
                name="IconSettingsGear2"
                size={16}
                className="text-heading opacity-40"
              />
              Settings
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem
              render={
                <a
                  href={NEUROSCALE_LINKS.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              }
            >
              <Icon
                name="IconGraduateCap"
                size={16}
                className="text-heading opacity-40"
              />
              Documentation
            </DropdownMenuItem>
            <DropdownMenuItem render={<a href={SUPPORT_MAILTO} />}>
              <Icon
                name="IconRescueRing"
                size={16}
                className="text-heading opacity-40"
              />
              Contact Support
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => logout.mutate(undefined)}>
            <Icon
              name="IconArrowBoxLeft"
              size={16}
              className="text-heading opacity-40"
            />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateOrganization
        open={createOrgOpen}
        onOpenChange={setCreateOrgOpen}
        hideTrigger
      />
    </>
  );
}
